import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { StatsCard } from "@/components/stats-card"
import { DataTable, ColumnHelper } from "@/components/data-table"
import { Modal, ModalContent, ModalHeader, ModalTitle } from "@/components/modal"
import { Drawer, DrawerHeader, DrawerTitle } from "@/components/drawer"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { SmartForm } from "@/components/smart-form"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import { z } from "zod"
import { Database, Plus, RefreshCw, Download, RotateCcw, Trash2, Eye } from "lucide-react"

const backupSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
})

const backupFormFields = [
  { name: "name", label: "Name", type: "text", placeholder: "Enter backup name" },
  { name: "description", label: "Description", type: "text", placeholder: "Enter description" },
]

export default function BackupsPage() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const [state, setState] = React.useState({
    isFormOpen: false,
    isViewOpen: false,
    isDeleteOpen: false,
    selectedItem: null,
    viewItem: null,
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["backups"],
    queryFn: () => api.get("/backups"),
  })

  const { data: stats } = useQuery({
    queryKey: ["backups-stats"],
    queryFn: () => api.get("/backups/stats"),
  })

  const createMutation = useMutation({
    mutationFn: (data) => api.post("/backups", data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(["backups"])
      addNotification({ type: "success", message: res.message || "Backup created successfully" })
      setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const restoreMutation = useMutation({
    mutationFn: (id) => api.post(`/backups/${id}/restore`),
    onSuccess: (res) => {
      queryClient.invalidateQueries(["backups"])
      addNotification({ type: "success", message: res.message || "Backup restored successfully" })
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/backups/${state.selectedItem.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["backups"])
      addNotification({ type: "success", message: "Backup deleted successfully" })
      setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const colHelper = ColumnHelper()
  const columns = React.useMemo(() => {
    const cols = [
      colHelper.text("id", { label: "ID" }),
      colHelper.text("name", { label: "Name" }),
      colHelper.text("description", { label: "Description" }),
      colHelper.number("size", { label: "Size" }),
      colHelper.text("created_by", { label: "Created By" }),
      colHelper.date("created_at", { label: "Created" }),
      colHelper.actions([
        {
          icon: <Eye className="h-4 w-4" />,
          onClick: (row) => setState((s) => ({ ...s, isViewOpen: true, viewItem: row })),
        },
        {
          icon: <RotateCcw className="h-4 w-4" />,
          onClick: (row) => restoreMutation.mutate(row.id),
        },
        {
          icon: <Download className="h-4 w-4" />,
          onClick: (row) => {
            window.open(`/api/backups/${row.id}/download`, "_blank")
          },
        },
        {
          icon: <Trash2 className="h-4 w-4" />,
          variant: "ghost",
          onClick: (row) => setState((s) => ({ ...s, isDeleteOpen: true, selectedItem: row })),
        },
      ]),
    ]
    return cols
  }, [])

  const formatBytes = (bytes) => {
    if (!bytes) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Backups</h1>
          <p className="text-muted-foreground">Manage database backups</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setState((s) => ({ ...s, isFormOpen: true, selectedItem: null }))}>
            <Plus className="mr-2 h-4 w-4" />
            Create Backup
          </Button>
        </div>
      </div>

      {stats?.data && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard title="Total Backups" value={stats.data.count || 0} icon={Database} />
          <StatsCard title="Total Size" value={formatBytes(stats.data.totalSize)} icon={Database} />
        </div>
      )}

      <DataTable
        columns={columns}
        data={data?.data || []}
        searchKey="name"
        searchPlaceholder="Search backups..."
        onRefresh={refetch}
        isLoading={isLoading}
      />

      <Drawer open={state.isFormOpen} onClose={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}>
        <DrawerHeader>
          <DrawerTitle>Create Backup</DrawerTitle>
        </DrawerHeader>
        <SmartForm
          schema={backupSchema}
          fields={backupFormFields}
          defaultValues={{}}
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}
          isLoading={createMutation.isPending}
        />
      </Drawer>

      <Modal open={state.isViewOpen} onOpenChange={(open) => setState((s) => ({ ...s, isViewOpen: open }))}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Backup Details</ModalTitle>
          </ModalHeader>
          <div className="space-y-4">
            {state.viewItem && Object.entries(state.viewItem).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-muted-foreground">{key}</span>
                <span className="font-medium">{String(value)}</span>
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        open={state.isDeleteOpen}
        onClose={() => setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Backup"
        description="Are you sure you want to delete this backup? This action cannot be undone."
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
